// Le chef d'orchestre : joue une palette (score.ts) mesure après mesure sur n'importe quel contexte Web Audio
// (en direct, ou hors-ligne pour les essais). Il compose en jouant : accords, basse, arpèges, mélodie tirée des
// leitmotivs (le monde, le héros, ses compagnons présents), percussions, cloches, bruits du monde ; et il ponctue
// les évènements (dés, combat, niveau, butin, mort, rencontre, appel du Conteur…).
import {
  Beds, makeImpulse, makeNoise, playBass, playGlissando, playNote, playPad, playPerc, playWahWah, sowTexture,
} from "./instruments";
import type { Bus, MelodicId, PercId } from "./instruments";
import { CLASS_INSTRUMENT, paletteOf } from "./score";
import type { Palette, PercStyle, Situation } from "./score";
import {
  barRhythm, degreeToMidi, dice, midiToFreq, MODES, motifFrom, nearestChordTone, triad, VEILLEE_CHORDS, VEILLEE_ROOT, VEILLEE_THEME,
} from "./theory";
import type { Dice, Motif } from "./theory";

export type Stinger =
  | { kind: "des"; success: boolean | null; crit: "succes" | "echec" | null }
  | { kind: "narration" | "niveau" | "butin" | "mort" | "appel" | "chronique" | "combatDebut" | "victoire" | "defaite" | "fuite" | "depart" | "arrivee" | "porte" | "allume" }
  | { kind: "rencontre"; other: string; classId: string | null }
  | { kind: "message"; from: string };

/** Durées en temps d'une mélodie de mesure : chaque note (degré, ou null pour un silence). */
interface Phrase { notes: { deg: number | null; beats: number }[] }

const LOOK_MIN = 0.05;

export class Conductor {
  readonly ctx: BaseAudioContext;
  private bus: Bus;
  private master: GainNode;
  private muffle: BiquadFilterNode;
  private reverbSend: GainNode;
  private beds: Beds;
  private situation: Situation | null = null;
  private palette: Palette | null = null;
  private nextBar = 0;
  private bar = 0;
  private lastDeg = 2;
  private themeStep = 0;
  private volume = 1;

  constructor(ctx: BaseAudioContext, destination: AudioNode) {
    this.ctx = ctx;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 12;
    comp.ratio.value = 3;
    comp.attack.value = 0.01;
    comp.release.value = 0.3;
    comp.connect(destination);
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(comp);
    this.muffle = ctx.createBiquadFilter();
    this.muffle.type = "lowpass";
    this.muffle.frequency.value = 20000;
    this.muffle.connect(this.master);
    const dry = ctx.createGain();
    dry.connect(this.muffle);
    const reverb = ctx.createConvolver();
    reverb.buffer = makeImpulse(ctx);
    this.reverbSend = ctx.createGain();
    this.reverbSend.gain.value = 0.5;
    this.reverbSend.connect(reverb);
    const wetReturn = ctx.createGain();
    wetReturn.gain.value = 0.55;
    reverb.connect(wetReturn).connect(this.muffle);
    this.bus = { ctx, dry, wet: this.reverbSend, noise: makeNoise(ctx) };
    const bedBus = ctx.createGain();
    bedBus.gain.value = 0.35;
    bedBus.connect(this.muffle);
    this.beds = new Beds(this.bus, bedBus);
  }

  /** Volume général (0 à 1), en douceur. */
  setVolume(v: number, at = this.ctx.currentTime): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyLevels(at, 1.2);
  }

  setSituation(s: Situation, at = this.ctx.currentTime): void {
    this.situation = s;
    const p = paletteOf(s);
    const changed = this.palette?.key !== p.key;
    const momentChanged = this.palette === null || this.paletteMoment !== s.moment;
    this.palette = p;
    this.paletteMoment = s.moment;
    if (!changed) return;
    if (momentChanged) {
      this.themeStep = 0;
      this.bar = 0;
    }
    this.beds.set(p.beds, at, momentChanged ? 1.5 : 4);
    this.applyLevels(at, momentChanged ? 1.5 : 3);
  }

  private paletteMoment: Situation["moment"] | null = null;

  private applyLevels(at: number, glide: number): void {
    const p = this.palette;
    if (!p) return;
    this.master.gain.cancelScheduledValues(at);
    this.master.gain.setTargetAtTime(0.62 * this.volume * p.volume, at, glide / 3);
    this.reverbSend.gain.setTargetAtTime(0.25 + p.reverb * 0.75, at, glide / 3);
    this.muffle.frequency.setTargetAtTime(p.muffle, at, glide / 3);
  }

  /** Programme les mesures jusqu'à `until` (horloge du contexte). Appelé souvent en direct, une fois hors-ligne. */
  schedule(until: number): void {
    if (!this.palette || !this.situation) return;
    if (this.nextBar < this.ctx.currentTime + LOOK_MIN) this.nextBar = this.ctx.currentTime + LOOK_MIN;
    while (this.nextBar < until) {
      this.nextBar += this.playBar(this.nextBar, this.palette, this.situation);
      this.bar++;
    }
  }

  /** Repartir de maintenant (après une mise en veille) sans rattraper les mesures perdues. */
  resync(): void {
    this.nextBar = this.ctx.currentTime + 0.1;
  }

  // ───────── une mesure ─────────

  private playBar(t: number, p: Palette, s: Situation): number {
    const beat = 60 / p.bpm;
    const barDur = beat * p.beats;
    const r = dice(`${s.world}|${p.key.length}|${this.bar}|${Math.floor(t * 10)}`);
    const n = MODES[p.mode].length;
    const inPhrase = this.bar % 8;
    const veillee = p.theme === "veillee";
    const chordIndex = veillee
      ? VEILLEE_CHORDS[this.themeStep % VEILLEE_CHORDS.length]
      : p.progression[Math.floor(this.bar / p.barsPerChord) % p.progression.length];
    const root = veillee ? VEILLEE_ROOT + 12 : p.root;
    const mode = veillee ? "dorien" : p.mode;
    const chord = triad(chordIndex);
    const deg = (d: number, octave = 0): number => midiToFreq(degreeToMidi(root, mode, d) + octave * 12);
    const chordStart = veillee || this.bar % p.barsPerChord === 0;
    const vel = 0.55 + p.intensity * 0.45;

    // nappe
    if (p.pad && chordStart) {
      const span = veillee ? barDur : barDur * p.barsPerChord;
      playPad(this.bus, p.pad, t, chord.map((d) => deg(d, -1)), span * 0.98, 0.8 + p.intensity * 0.3, p.brightness);
    }

    // basse
    if (p.bass) this.bassLine(p, t, beat, barDur, chordIndex, chordStart, deg, vel, r);

    // arpèges
    if (p.arp) this.arpeggio(p, t, beat, chord, deg, vel, r);

    // mélodie
    if (veillee) {
      this.veilleeBar(t, beat, p);
    } else if (p.lead) {
      const phrase = this.composeBar(p, s, chordIndex, n, r, inPhrase);
      const instrument = this.voiceFor(p, inPhrase);
      if (phrase && instrument) this.playPhrase(phrase, instrument, t, beat, deg, vel * (inPhrase === 6 ? 1.05 : 0.95), inPhrase % 2 ? 0.25 : -0.15);
    }

    // leitmotivs des compagnons présents : ils répondent quand la mélodie respire
    if (!veillee && s.companions.length > 0 && (inPhrase === 3 || inPhrase === 7) && r.chance(0.8)) {
      const other = s.companions[Math.floor(this.bar / 8) % s.companions.length];
      const m = motifFrom(other.name);
      const inst = other.classId ? CLASS_INSTRUMENT[other.classId as keyof typeof CLASS_INSTRUMENT] : "luth";
      const anchor = nearestChordTone(chordIndex, 4, n);
      this.playPhrase(this.fitMotif(m, anchor, p.beats), inst, t, beat, deg, vel * 0.7, 0.55);
    }

    // percussions
    if (p.perc) this.drums(p.perc, p.beats, t, beat, vel, r);

    // cloches éparses
    if (p.sparkle && r.chance(0.35 + p.intensity * 0.2)) {
      const count = r.int(1, 3);
      for (let i = 0; i < count; i++) {
        const d = r.pick(chord) + n * (1 + (r.chance(0.5) ? 1 : 0));
        playNote(this.bus, p.sparkle, t + r.int(0, p.beats - 1) * beat + (r.chance(0.3) ? beat / 2 : 0), deg(d), beat, 0.5 * vel, r.next() * 1.4 - 0.7);
      }
    }

    // le Conteur écrit : la harpe tisse au-dessus de tout
    if (p.weaving && this.bar % 2 === 0) {
      const notes = [0, 2, 4, 7, 9, 11, 14].map((d) => deg(chordIndex + d, 1));
      playGlissando(this.bus, "harpe", t + beat * 0.5, notes, Math.min(0.11, barDur / 10), 0.28, 0.3);
    }

    if (p.heartbeat) {
      for (let k = 0; k * 1.05 < barDur; k++) playPerc(this.bus, "coeur", t + k * 1.05, 0.9);
    }
    if (p.tick) {
      for (let b = 0; b < p.beats; b++) playPerc(this.bus, "tic", t + b * beat, 0.5 + (b / p.beats) * 0.5, 0.2);
    }

    // bruits du monde semés sur la mesure
    for (const [id, amount] of Object.entries(p.textures)) {
      if (amount && amount > 0) sowTexture(this.bus, id as keyof Palette["textures"], t, barDur, amount, r.next);
    }

    if (veillee) this.themeStep++;
    return barDur;
  }

  private bassLine(p: Palette, t: number, beat: number, barDur: number, chordIndex: number, chordStart: boolean,
    deg: (d: number, o?: number) => number, vel: number, r: Dice): void {
    const low = (d: number): number => deg(d, -2);
    const fifth = chordIndex + 4;
    switch (p.bass) {
      case "bourdon":
        if (chordStart) playBass(this.bus, t, low(chordIndex), barDur * p.barsPerChord * 0.98, vel, true);
        return;
      case "ronde":
        playBass(this.bus, t, low(chordIndex), beat * (p.beats === 6 ? 3 : 2), vel);
        if (p.beats === 4) playBass(this.bus, t + 2 * beat, low(r.chance(0.6) ? fifth : chordIndex), beat * 2, vel * 0.8);
        if (p.beats === 6) playBass(this.bus, t + 3 * beat, low(fifth), beat * 3, vel * 0.8);
        return;
      case "marche":
        for (let b = 0; b < p.beats; b += p.beats === 3 ? 1 : 2) {
          playBass(this.bus, t + b * beat, low(b % 4 === 0 ? chordIndex : fifth), beat * 1.6, vel * (b === 0 ? 1 : 0.8));
        }
        return;
      case "oompa":
        playBass(this.bus, t, low(chordIndex), beat * 0.8, vel);
        if (p.beats === 4) playBass(this.bus, t + 2 * beat, low(fifth), beat * 0.8, vel * 0.85);
        for (let b = 1; b < p.beats; b++) {
          if (p.beats === 4 && b === 2) continue;
          // le « pa » : l'accord en pizzicati
          for (const d of triad(chordIndex)) playNote(this.bus, "pizz", t + b * beat, deg(d, -1), beat * 0.5, vel * 0.45, -0.3);
        }
        return;
      case "ostinato": {
        const pattern = [0, 0, 0, 1, 0, 0, 4, 3];
        const steps = p.beats * 2;
        for (let i = 0; i < steps; i++) {
          playBass(this.bus, t + i * beat / 2, low(chordIndex + pattern[i % pattern.length]), beat * 0.45, vel * (i % 2 ? 0.7 : 1));
        }
        return;
      }
      default:
        return;
    }
  }

  private arpeggio(p: Palette, t: number, beat: number, chord: number[], deg: (d: number, o?: number) => number, vel: number, r: Dice): void {
    if (!p.arp) return;
    const steps = p.beats === 6 ? 6 : p.beats * 2;
    const step = p.beats === 6 ? beat : beat / 2;
    const tones = [chord[0], chord[1], chord[2], chord[0] + 7, chord[1] + 7];
    for (let i = 0; i < steps; i++) {
      if (p.intensity < 0.35 && i % 2 === 1) continue; // la nuit, un arpège plus rare
      let d: number;
      switch (p.arpStyle) {
        case "montant": d = tones[i % tones.length]; break;
        case "vague": d = tones[i % 8 < 4 ? i % 4 : 4 - (i % 4)]; break;
        default: d = i % 2 === 0 ? chord[0] : r.pick([chord[1], chord[2]]); break;
      }
      playNote(this.bus, p.arp, t + i * step, deg(d, p.arp === "pizz" ? -1 : 0), step * 1.5, vel * (i === 0 ? 0.5 : 0.36), -0.35);
    }
  }

  /** Quel instrument chante cette mesure : appel du soliste, réponse de l'autre voix. */
  private voiceFor(p: Palette, inPhrase: number): MelodicId | null {
    if (p.answer && (inPhrase === 2 || inPhrase === 3)) return p.answer;
    return p.lead;
  }

  /**
   * La mélodie d'une mesure. Phrase de huit mesures : motif (0), suite (1), motif varié (2), souffle (3),
   * motif (4), suite (5), sommet (6), cadence sur la tonique (7). Le motif est celui du monde, ou du héros.
   */
  private composeBar(p: Palette, s: Situation, chord: number, n: number, r: Dice, inPhrase: number): Phrase | null {
    if (inPhrase === 3 && (s.companions.length > 0 || r.chance(0.6))) return null;
    if (inPhrase !== 0 && inPhrase !== 7 && !r.chance(p.density)) return null;
    const motifText = p.theme === "heros" && s.hero ? s.hero : `${s.worldName}|${s.world}`;
    const motif = motifFrom(motifText);
    const anchor = nearestChordTone(chord, this.lastDeg, n);
    let phrase: Phrase;
    switch (inPhrase) {
      case 0:
      case 4:
        phrase = this.fitMotif(motif, anchor, p.beats);
        break;
      case 2:
        // le motif renversé, comme une question
        phrase = this.fitMotif({ degrees: motif.degrees.map((d) => -d), beats: motif.beats }, anchor + 2, p.beats);
        break;
      case 7:
        phrase = { notes: this.improvise(p, chord, n, r, true) };
        break;
      default:
        phrase = { notes: this.improvise(p, chord, n, r, false, inPhrase === 6 ? 3 : 0) };
    }
    const last = [...phrase.notes].reverse().find((x) => x.deg !== null);
    if (last && last.deg !== null) this.lastDeg = last.deg;
    return phrase;
  }

  /** Le motif posé sur la mesure : durées étirées ou serrées pour tenir pile dans la mesure. */
  private fitMotif(m: Motif, anchor: number, beats: number): Phrase {
    const total = m.beats.reduce((a, b) => a + b, 0);
    const k = beats / total;
    let base = anchor;
    const hi = Math.max(...m.degrees);
    const lo = Math.min(...m.degrees);
    if (base + hi > 10) base -= 7;
    if (base + lo < -4) base += 7;
    return { notes: m.degrees.map((d, i) => ({ deg: base + d, beats: m.beats[i] * k })) };
  }

  private improvise(p: Palette, chord: number, n: number, r: Dice, cadence: boolean, lift = 0): Phrase["notes"] {
    const rhythm = barRhythm(p.beats, r, p.density);
    const notes: Phrase["notes"] = [];
    let d = this.lastDeg + lift;
    rhythm.forEach((len, i) => {
      const lastNote = i === rhythm.length - 1;
      if (cadence && lastNote) {
        d = nearestChordTone(chord, d, n);
        d = Math.round(d / n) * n; // la tonique la plus proche
      } else if (i === 0 || len >= 2) {
        d = nearestChordTone(chord, d + r.pick([0, 1, -1, 2]), n);
      } else {
        d += r.pick([1, -1, 1, -1, 2, -2, 0]);
      }
      if (d > 11) d -= 2;
      if (d < -4) d += 2;
      notes.push({ deg: d, beats: len });
    });
    return notes;
  }

  private playPhrase(ph: Phrase, inst: MelodicId, t: number, beat: number, deg: (d: number, o?: number) => number, vel: number, pan: number): void {
    let at = t;
    for (const note of ph.notes) {
      const dur = note.beats * beat;
      if (note.deg !== null) playNote(this.bus, inst, at, deg(note.deg), dur * 0.95, vel, pan);
      at += dur;
    }
  }

  /** Une mesure du thème « La Veillée » : la harpe le dit, la flûte le reprend, puis les deux ensemble. */
  private veilleeBar(t: number, beat: number, p: Palette): void {
    const totalBeats = VEILLEE_THEME.reduce((a, x) => a + x.beats, 0);
    const pass = Math.floor((this.themeStep * 3) / totalBeats);
    const from = (this.themeStep * 3) % totalBeats;
    let pos = 0;
    const lead: MelodicId = pass % 3 === 1 ? "flute" : "harpe";
    for (const note of VEILLEE_THEME) {
      if (pos >= from && pos < from + 3 && note.midi !== null) {
        const at = t + (pos - from) * beat;
        playNote(this.bus, lead, at, midiToFreq(note.midi), note.beats * beat * 0.95, 0.85, -0.1);
        if (pass % 3 === 2) playNote(this.bus, "flute", at, midiToFreq(note.midi + 12), note.beats * beat * 0.95, 0.5, 0.3);
        if (p.sparkle && note.beats >= 2) playNote(this.bus, p.sparkle, at, midiToFreq(note.midi + 12), beat, 0.35, 0.4);
      }
      pos += note.beats;
    }
  }

  private drums(style: PercStyle, beats: number, t: number, beat: number, vel: number, r: Dice): void {
    const hit = (id: PercId, pos: number, v: number, pan = 0): void => {
      if (pos < beats) playPerc(this.bus, id, t + pos * beat, v * vel, pan);
    };
    switch (style) {
      case "marche":
        hit("tambour", 0, 1);
        hit("tambour", beats === 6 ? 3 : 2, 0.65);
        for (let i = 0; i < beats * 2; i++) hit("shaker", i / 2, i % 2 ? 0.35 : 0.55, 0.4);
        return;
      case "danse":
        if (beats === 6) {
          hit("tambour", 0, 1); hit("tambour", 3, 0.7); hit("mains", 2, 0.6, 0.3); hit("mains", 5, 0.6, 0.3);
          for (let i = 0; i < 6; i++) hit("shaker", i, 0.4, -0.4);
        } else {
          hit("tambour", 0, 1);
          for (let b = 1; b < beats; b++) hit(b === 2 && beats === 4 ? "tambour" : "mains", b, 0.6, 0.3);
          for (let i = 0; i < beats * 2; i++) hit("shaker", i / 2, i % 2 ? 0.45 : 0.3, -0.4);
          if (r.chance(0.4)) hit("darbouka", beats - 0.5, 0.5, -0.2);
        }
        return;
      case "bataille":
        hit("taiko", 0, 1);
        hit("taiko", beats === 6 ? 3 : 2, 0.8);
        if (beats === 4) hit("taiko", 1.5, 0.55);
        if (r.chance(0.5)) hit("taiko", beats - 0.5, 0.5);
        for (let i = 0; i < beats * 2; i++) hit("tambour", i / 2, i % 2 ? 0.35 : 0.5, i % 2 ? 0.3 : -0.3);
        return;
      case "valse":
        hit("tac", 0, 0.7); hit("tic", 1, 0.45); hit("tic", 2, 0.45);
        return;
      case "shanty":
        hit("pied", 0, 1); hit("pied", 2, 0.45); hit("mains", 3, 0.85, 0.2); hit("pied", 5, 0.4);
        return;
      case "horloge":
        hit("tac", 0, 0.7, -0.3);
        for (let i = 1; i < beats * 2; i++) hit("tic", i / 2, i % 2 ? 0.3 : 0.5, 0.3);
        return;
      case "jungle":
        hit("tambour", 0, 1); hit("tambour", beats * 0.375, 0.6); hit("tambour", beats * 0.75, 0.7);
        for (const pos of [0.5, 2, 3.5]) hit("bois", (pos * beats) / 4, 0.6, 0.4);
        for (let i = 0; i < beats * 2; i++) hit("shaker", i / 2, 0.35, -0.4);
        return;
      case "darbouka":
        hit("tambour", 0, 0.9); hit("darbouka", 1, 0.6, 0.2); hit("darbouka", 1.5, 0.4, 0.2);
        hit("tambour", 2, 0.7); hit("darbouka", 3, 0.6, 0.2); hit("darbouka", 3.5, 0.4, -0.2);
        return;
      case "lent":
        if (this.bar % 2 === 0) hit("taiko", 0, 0.7);
        return;
      case "oompa":
        for (let b = 1; b < beats; b += 2) hit("bois", b, 0.5, 0.3);
        return;
      case "furtif":
        for (let i = 0; i < beats * 2; i++) if (r.chance(0.45)) hit(i % 3 ? "tic" : "bois", i / 2, 0.35, r.next() - 0.5);
        return;
    }
  }

  // ───────── ponctuations ─────────

  stinger(st: Stinger, at = this.ctx.currentTime + 0.03): void {
    const p = this.palette;
    const s = this.situation;
    if (!p || !s) return;
    const root = p.theme === "veillee" ? VEILLEE_ROOT + 12 : p.root;
    const mode = p.theme === "veillee" ? "dorien" : p.mode;
    const f = (d: number, o = 0): number => midiToFreq(degreeToMidi(root, mode, d) + o * 12);
    const b = this.bus;
    switch (st.kind) {
      case "des": {
        // les dés roulent (1,7 s, comme à l'écran) puis le verdict
        const r = dice(`des|${at}`);
        for (let i = 0; i < 9; i++) {
          const when = at + 1.6 * (1 - Math.pow(1 - i / 9, 1.6));
          playNote(b, "harpe", when, f(r.int(0, 9), 1), 0.2, 0.32, r.next() - 0.5);
        }
        const v = at + 1.7;
        if (st.crit === "succes") {
          playGlissando(b, "celesta", v, [0, 2, 4, 7, 9, 11, 14].map((d) => f(d, 1)), 0.05, 0.8);
          playNote(b, "cor", v, f(0), 1.2, 0.9);
          playNote(b, "cor", v, f(4), 1.2, 0.7);
          playNote(b, "cloche", v + 0.35, f(7, 1), 2, 0.8);
          playPerc(b, "taiko", v, 0.8);
        } else if (st.crit === "echec") {
          if (s.tone === "humoristique") playWahWah(b, v, f(4, 0), 1);
          else {
            playNote(b, "glas", v, f(0, -1), 3, 0.9);
            playNote(b, "basson", v, midiToFreq(degreeToMidi(root, mode, 0) - 6), 1.2, 0.8);
            playPerc(b, "taiko", v, 0.6);
          }
        } else if (st.success === true) {
          playNote(b, "harpe", v, f(4), 0.6, 0.8);
          playNote(b, "harpe", v + 0.18, f(7), 1.2, 0.9);
          playNote(b, "celesta", v + 0.18, f(7, 1), 1.2, 0.6);
        } else if (st.success === false) {
          playNote(b, "pizz", v, f(1, 0), 0.3, 0.8);
          playNote(b, "basson", v + 0.2, f(0, -1), 0.8, 0.7);
        } else {
          playNote(b, "harpe", v, f(0, 1), 0.8, 0.7);
        }
        return;
      }
      case "narration":
        playGlissando(b, "harpe", at, [0, 2, 4, 7, 9].map((d) => f(d)), 0.07, 0.45, 0.2);
        return;
      case "allume":
        playGlissando(b, "harpe", at, [0, 2, 4, 6, 7, 9, 11, 14].map((d) => f(d)), 0.08, 0.6);
        playNote(b, "cloche", at + 0.7, f(7, 1), 3, 0.6);
        return;
      case "niveau":
      case "victoire": {
        const notes = [0, 2, 4, 7];
        const rhythm = [0, 0.16, 0.32, 0.5];
        notes.forEach((d, i) => {
          playNote(b, "cor", at + rhythm[i], f(d), i === 3 ? 1.6 : 0.15, 0.95, -0.2);
          playNote(b, "cor", at + rhythm[i], f(d + 2), i === 3 ? 1.6 : 0.15, 0.6, 0.2);
        });
        for (let i = 0; i < 8; i++) playPerc(b, "taiko", at + i * 0.06, 0.18 + i * 0.06);
        playPerc(b, "taiko", at + 0.5, 1);
        playGlissando(b, "celesta", at + 0.5, [7, 9, 11, 14, 16].map((d) => f(d)), 0.06, 0.6);
        return;
      }
      case "butin": {
        const r = dice(`butin|${at}`);
        for (let i = 0; i < 6; i++) playNote(b, "boite", at + i * 0.07 + r.next() * 0.03, f(r.pick([0, 2, 4, 7]), 1), 0.3, 0.6, r.next() - 0.5);
        return;
      }
      case "mort":
        for (let i = 0; i < 3; i++) playNote(b, "glas", at + i * 1.7, f(0, -1), 5, 0.9);
        playPad(b, "choeur", at, [f(0, -1), f(2, -1), f(4, -1)], 5, 1);
        return;
      case "appel":
        // le cor du Conteur : une quarte qui monte, l'écho la reprend
        playNote(b, "cor", at, f(0), 0.45, 0.85);
        playNote(b, "cor", at + 0.5, f(3), 1.4, 0.95);
        playNote(b, "cor", at + 2.1, f(3), 1.0, 0.35, 0.6);
        return;
      case "chronique": {
        const m = motifFrom(`${s.worldName}|${s.world}`);
        let when = at;
        m.degrees.forEach((d, i) => {
          playNote(b, "cloche", when, f(d + 2), 2, 0.7);
          playNote(b, "harpe", when, f(d + 2, -1), 1, 0.5);
          when += m.beats[i] * 0.4;
        });
        return;
      }
      case "combatDebut":
        playPerc(b, "taiko", at, 1);
        playPerc(b, "taiko", at + 0.18, 0.8);
        playPad(b, "cordes", at, [f(0, -1), f(1, -1), f(4, -1)], 0.9, 1.4, 0.9);
        playNote(b, "cor", at + 0.18, f(0), 0.7, 1);
        return;
      case "defaite":
        [4, 3, 1, 0].forEach((d, i) => playNote(b, "violon", at + i * 0.5, f(d), 0.55, 0.8));
        playNote(b, "glas", at + 2, f(0, -1), 4, 0.7);
        return;
      case "fuite":
        for (let i = 0; i < 8; i++) playNote(b, "pizz", at + i * 0.06, f(7 - i), 0.1, 0.7, 0.4 - i * 0.1);
        return;
      case "depart":
        playNote(b, "flute", at, f(0), 0.35, 0.8);
        playNote(b, "flute", at + 0.4, f(3), 0.35, 0.8);
        playNote(b, "flute", at + 0.8, f(4), 1.2, 0.9);
        return;
      case "arrivee":
        playGlissando(b, "harpe", at, [0, 2, 4, 7].map((d) => f(d, -1)), 0.05, 0.7);
        playNote(b, "cloche", at + 0.25, f(0, 1), 2, 0.5);
        return;
      case "porte":
        playPerc(b, "taiko", at, 0.5);
        playPad(b, "brume", at, [f(0, -2), f(1, -2)], 2.5, 1.2, 0.2);
        return;
      case "rencontre": {
        // deux leitmotivs qui se croisent enfin
        const mine = s.hero ? motifFrom(s.hero) : motifFrom(s.worldName);
        const theirs = motifFrom(st.other);
        const inst = st.classId && st.classId in CLASS_INSTRUMENT ? CLASS_INSTRUMENT[st.classId as keyof typeof CLASS_INSTRUMENT] : "flute";
        let when = at;
        mine.degrees.forEach((d, i) => { playNote(b, "harpe", when, f(d), 0.6, 0.7, -0.4); when += mine.beats[i] * 0.3; });
        when = at + 0.15;
        theirs.degrees.forEach((d, i) => { playNote(b, inst, when, f(d + 2), mine.beats[i % mine.beats.length] * 0.3, 0.6, 0.4); when += theirs.beats[i] * 0.3; });
        playNote(b, "cloche", at + 1.2, f(4, 1), 2.5, 0.6);
        return;
      }
      case "message": {
        const m = motifFrom(st.from);
        playNote(b, "harpe", at, f(m.degrees[0] + 4, 1), 0.3, 0.32, 0.5);
        playNote(b, "harpe", at + 0.12, f(m.degrees[1] + 4, 1), 0.5, 0.28, 0.5);
        return;
      }
    }
  }
}

/** Rend `seconds` de musique d'une situation hors-ligne (essais, démos) ; renvoie le tampon stéréo. */
export async function renderOffline(s: Situation, seconds: number, stingers: { at: number; st: Stinger }[] = [], sampleRate = 44100): Promise<AudioBuffer> {
  const ctx = new OfflineAudioContext(2, Math.ceil(seconds * sampleRate), sampleRate);
  const c = new Conductor(ctx, ctx.destination);
  c.setSituation(s, 0);
  c.setVolume(1, 0);
  c.schedule(seconds);
  for (const { at, st } of stingers) c.stinger(st, at);
  return ctx.startRendering();
}
